import {
  ArrowRightOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FBOHeaderBar from "../../components/element/FBOHeaderBar";
import NavigationBar from "../../components/element/NavigationBar";
import SnackbarAlert from "../../components/element/SnackbarAlert";
import { useFormData } from "../../helpers/FormDataProvider";
import useFBOApi from "../../services/api/useFBOApi";
import {
  DATE_FORMAT,
  PAGINATION_PAGE_SIZE,
  PAYMENT_CONSTANTS,
  PAYMENT_DIALOG,
  PAYMENT_FORM_FIELDS,
  PAYMENT_FORM_VALUES,
  CURRENCY_CODES,
  FBO_TYPE_URL_PARAMS,
  EMPTY_STRING,
} from "../../utils/constants";
import ROUTE_PATHS from "../../utils/routePaths";
import { getUrls } from "../../utils/urls";
import { getInvoiceStatus, getInvoicingFBOType } from "../../utils/utils";
import InvoicesTable from "./Tables/InvoicesTable";
import InvoicesTabBar from "./Views/InvoicesTabBar";
import { getServiceUrls } from "../../services/serviceUrls";
import DateLocalizationProvider from "../../components/element/DateLocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers";
const Invoices = () => {
  const SERVICE_URL = getServiceUrls();
  const navigate = useNavigate();
  const { aboType } = useParams();
  const { fetchFBOInvoices, submitWireTransferPayment, fetchBankConfig, downloadFBOInvoice } =
    useFBOApi();
  const [selectedTab, setSelectedTab] = useState(0);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(
    PAYMENT_CONSTANTS.DEFAULT_PAYMENT_TYPE
  );
  const { userData } = useFormData();
  const [currentPage, setCurrentPage] = useState(() => {
    return sessionStorage.getItem("fboInvoicesCurrentPage")
      ? Number(sessionStorage.getItem("fboInvoicesCurrentPage"))
      : 0;
  });
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState(EMPTY_STRING);
  const [filterInvoiceDate, setFilterInvoiceDate] = useState(null);
  const pageSize = PAGINATION_PAGE_SIZE;
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [openWireReferenceDialog, setOpenWireReferenceDialog] = useState(false);
  const [wireReference, setWireReference] = useState(EMPTY_STRING);
  const [wirePaymentAmount, setWirePaymentAmount] = useState(0);
  const [wirePaymentDate, setWirePaymentDate] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: EMPTY_STRING,
    type: "error",
  });
  const [selectedBank, setSelectedBank] = useState(CURRENCY_CODES.BSD);
  const [openBankDetailsDialog, setOpenBankDetailsDialog] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const queryClient = useQueryClient();

  const {
    data: invoicingData,
    error,
    isLoading,
  } = useQuery({
    queryKey: [
      "fbo-invoices-list",
      aboType,
      filterInvoiceNumber,
      filterInvoiceDate,
      currentPage,
      selectedTab,
    ],
    queryFn: () => {
      const params = {
        fboInvoiceType: getInvoicingFBOType(aboType),
        status: getInvoiceStatus(selectedTab),
        page: currentPage,
        size: pageSize,
      };
      if (filterInvoiceNumber !== EMPTY_STRING) {
        params.invoiceNumber = filterInvoiceNumber;
      }
      if (filterInvoiceDate) {
        params.invoiceDate = filterInvoiceDate;
      }
      return fetchFBOInvoices(params);
    },
    retry: false,
  });

  const { data: bankConfig, isLoading: bankConfigLoading } = useQuery({
    queryKey: ["bankConfig"],
    queryFn: () => fetchBankConfig(SERVICE_URL.REACT_APP_PROFILE_ENV),
  });

  useEffect(() => {
    sessionStorage.setItem("fboInvoicesCurrentPage", currentPage);
  }, [currentPage]);

  // Reset page to 0 when tab changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedTab]);

  // Reset selected invoices when tab changes
  useEffect(() => {
    setSelectedInvoices([]);
  }, [selectedTab]);

  // Reset filters when tab changes
  useEffect(() => {
    setFilterInvoiceNumber(EMPTY_STRING);
    setFilterInvoiceDate(null);
  }, [selectedTab]);

  const wireTransferMutation = useMutation({
    mutationFn: (paymentData) => submitWireTransferPayment(paymentData),
    onSuccess: () => {
      setOpenWireReferenceDialog(false);
      setWireReference(EMPTY_STRING);
      setWirePaymentAmount(0);
      setWirePaymentDate(null);
      setSelectedBank(CURRENCY_CODES.BSD);
      setSelectedInvoices([]);

      queryClient.invalidateQueries({
        queryKey: ["fbo-invoices-list"],
      });

      setCurrentPage(0);
    },
  });

  const downloadInvoiceMutation = useMutation({
    mutationFn: ({ dynamicsInvoiceId }) => {
      setDownloadingInvoiceId(dynamicsInvoiceId);
      return downloadFBOInvoice(dynamicsInvoiceId);
    },
    onSuccess: () => {
      setDownloadingInvoiceId(null);
      setSnackbar({
        open: true,
        message: "Invoice downloaded successfully",
        type: "success",
      });
    },
    onError: (error) => {
      setDownloadingInvoiceId(null);
      setSnackbar({
        open: true,
        message: error?.errorMessage || error?.message || "Failed to download invoice",
        type: "error",
      });
    },
  });

  const handleViewInvoiceClick = (invoiceId) => {
    navigate(
      ROUTE_PATHS.VIEW_INVOICES.replace(":aboType", aboType).replace(
        ":invoiceId",
        invoiceId
      )
    );
  };

  const handleApplyFilter = (selectedDate, invoiceNumber) => {
    if (selectedDate !== null) {
      setFilterInvoiceDate(dayjs(selectedDate).format("YYYY-MM-DD"));
    }
    if (invoiceNumber !== EMPTY_STRING) {
      setFilterInvoiceNumber(invoiceNumber);
    }
    setCurrentPage(0);
  };

  const handleClearFilter = () => {
    setFilterInvoiceDate(null);
    setFilterInvoiceNumber(EMPTY_STRING);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    if (currentPage + 1 < invoicingData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePayClick = () => {
    if (selectedInvoices.length === 0) {
      setSnackbar({ open: false, message: EMPTY_STRING });
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: "No Invoice is selected",
        });
      }, 100);
      return;
    }
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
  };

  const handleInvoiceSelection = (selected) => {
    setSelectedInvoices(selected);
  };

  const calculateSelectedTotal = () => {
    if (!selectedInvoices.length) {
      return 0;
    }
    return selectedInvoices.reduce((total, invoice) => {
      return total + ((parseFloat(invoice.totalAmount) || 0) - (parseFloat(invoice.totalPaymentReceived) || 0));
    }, 0);
  };

  const getSelectedInvoiceNumbers = () => {
    return selectedInvoices
      .map((invoice) => invoice.dynamicsInvoiceNumber)
      .join(",");
  };

  const handlePaymentSubmit = () => {
    if (selectedPaymentType === PAYMENT_CONSTANTS.PAYMENT_TYPES.WIRE) {
      setOpenPaymentDialog(false);
      setOpenBankDetailsDialog(true);
    } else {
      setOpenPaymentDialog(false);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = getUrls().PAYMENT_URL;
      form.target = "_blank";
      form.style.display = "none";

      const fields = {
        [PAYMENT_FORM_FIELDS.GATEWAY_ACCOUNT]:
          getUrls().PAYMENT_GATEWAY_ACCOUNT,
        [PAYMENT_FORM_FIELDS.AMOUNT]: calculateSelectedTotal().toFixed(2),
        [PAYMENT_FORM_FIELDS.CURRENCY]: PAYMENT_CONSTANTS.CURRENCY,
        [PAYMENT_FORM_FIELDS.CUSTOM_NAME_1]: PAYMENT_FORM_VALUES.CUSTOM_NAME_1,
        [PAYMENT_FORM_FIELDS.CUSTOM_VALUE_1]: getSelectedInvoiceNumbers(),
        [PAYMENT_FORM_FIELDS.CUSTOM_NAME_2]: PAYMENT_FORM_VALUES.CUSTOM_NAME_2,
        [PAYMENT_FORM_FIELDS.CUSTOM_VALUE_2]:
          userData?.associations?.[0]?.fboCustomer?.dynamicsCustomerNumber,
        [PAYMENT_FORM_FIELDS.PAYMENT_TYPE]: PAYMENT_FORM_VALUES.PAYMENT_TYPE,
        [PAYMENT_FORM_FIELDS.BILLING_COUNTRY]: PAYMENT_CONSTANTS.COUNTRY,
        [PAYMENT_FORM_FIELDS.RESPONSE_URL]: getUrls().PAYMENT_RESPONSE_URL,
        [PAYMENT_FORM_FIELDS.EMAIL]: userData?.email,
        [PAYMENT_FORM_FIELDS.TRANSITION_TYPE]:
          PAYMENT_CONSTANTS.TRANSITION_TYPE,
        [PAYMENT_FORM_FIELDS.SUBMIT]: PAYMENT_FORM_VALUES.SUBMIT,
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      const submitButton = document.createElement("input");
      submitButton.type = "submit";
      submitButton.style.display = "none";
      form.appendChild(submitButton);

      const handleFocus = () => {
        window.location.reload();
        window.removeEventListener("focus", handleFocus);
      };
      window.addEventListener("focus", handleFocus);

      document.body.appendChild(form);
      submitButton.click();
      form.remove();
    }
  };

  const handleWireReferenceSubmit = () => {
    const paymentData = {
      paymentReferenceNumber: wireReference,
      bankAccountNumber:
        selectedBank === CURRENCY_CODES.BSD
          ? bankConfig?.data?.bsdAccountNumber
          : bankConfig?.data?.usdAccountNumber,
      fboInvoiceIds: selectedInvoices.map((invoice) => invoice.id),
      paymentDate: dayjs(wirePaymentDate).format("YYYY-MM-DD"),
      paymentAmount: wirePaymentAmount,
    };

    wireTransferMutation.mutate(paymentData);
  };
  const handleBankDetailsContinue = () => {
    setOpenBankDetailsDialog(false);
    setOpenWireReferenceDialog(true);
  };

  const handleDownloadInvoice = (row) => {
    downloadInvoiceMutation.mutate({
      dynamicsInvoiceId: row.dynamicsInvoiceId,
    });
  };

  return (
    <div>
      <NavigationBar />
      <FBOHeaderBar
        title={"Invoices"}
        backContent={
          <div className="navigation-header">
            <Link to={ROUTE_PATHS.FBO_HOME} className="navigation-header-link">
              Home/
            </Link>
            <Link
              to={
                aboType === FBO_TYPE_URL_PARAMS.LANDING_FEE
                  ? ROUTE_PATHS.LANDING_FEE_HOME
                  : aboType === FBO_TYPE_URL_PARAMS.FUEL_ROYALTY
                    ? ROUTE_PATHS.FUEL_ROYALTY_HOME
                    : ROUTE_PATHS.AIRPORT_BASED_OPERATORS_HOME
              }
              className="navigation-header-link"
            >
              {aboType === FBO_TYPE_URL_PARAMS.LANDING_FEE
                ? "Landing Fee/"
                : aboType === FBO_TYPE_URL_PARAMS.FUEL_ROYALTY
                  ? "Fuel Royalty/"
                  : "Airport Based Operators - ABOs/"}
            </Link>
            Invoices
          </div>
        }
      />

      <InvoicesTabBar
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        onClickApply={handleApplyFilter}
        onClickClear={handleClearFilter}
        bankConfig={bankConfig}
      />

      {(selectedTab === 0 || selectedTab === 2) && (
        <div>
          <div className="invoices-grand-total-container">
            <div className="invoices-grand-total-text">
              Grand Total: BSD {calculateSelectedTotal().toFixed(2)}
            </div>
            <div className="payment-helper-text">
              When you click the "Pay" button, you will be redirected to the
              next screen to complete your payment.
            </div>
            <Button
              variant="contained"
              color="approve"
              onClick={handlePayClick}
              endIcon={<ArrowRightOutlined style={{ fontSize: "15px" }} />}
            >
              Pay
            </Button>
          </div>
        </div>
      )}

      <InvoicesTable
        invoiceData={invoicingData?.data}
        isLoading={isLoading}
        error={error}
        onViewClick={handleViewInvoiceClick}
        onSelectionChange={handleInvoiceSelection}
        selectedTab={selectedTab}
        selectedInvoices={selectedInvoices}
        currentPage={currentPage}
        pageSize={pageSize}
        onDownloadInvoice={handleDownloadInvoice}
        downloadingInvoiceId={downloadingInvoiceId}
        showLandUseCategory={
          aboType === FBO_TYPE_URL_PARAMS.AIRPORT_BASED_OPERATORS
        }
      />

      {invoicingData?.data?.content &&
        invoicingData?.data?.content.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-content">
              Page {currentPage + 1} of {invoicingData?.data?.totalPages}
            </div>
            <button
              className="pagination-button"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              <CaretLeftOutlined />
            </button>
            <button
              className="pagination-button"
              onClick={handleNextPage}
              disabled={currentPage + 1 === invoicingData?.data?.totalPages}
            >
              <CaretRightOutlined />
            </button>
          </div>
        )}

      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog}>
        <DialogContent>
          <div className="payment-options-container">
            <h2>Payment Options</h2>
            <RadioGroup
              defaultValue={PAYMENT_CONSTANTS.DEFAULT_PAYMENT_TYPE}
              value={selectedPaymentType}
              onChange={(e) => setSelectedPaymentType(e.target.value)}
            >
              <FormControlLabel
                value={PAYMENT_CONSTANTS.PAYMENT_TYPES.WIRE}
                control={<Radio />}
                label={PAYMENT_CONSTANTS.PAYMENT_LABELS.WIRE}
              />
              <FormControlLabel
                value={PAYMENT_CONSTANTS.PAYMENT_TYPES.CREDIT}
                control={<Radio />}
                label={PAYMENT_CONSTANTS.PAYMENT_LABELS.CREDIT}
              />
            </RadioGroup>
            <Button
              variant="contained"
              fullWidth
              onClick={handlePaymentSubmit}
              sx={{ mt: 2 }}
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openWireReferenceDialog}
        onClose={() => setOpenWireReferenceDialog(false)}
      >
        <DialogContent>
          <div className="wire-reference-container">
            <h2 style={{ textAlign: "center" }}>
              {PAYMENT_DIALOG.WIRE_REFERENCE.TITLE}
            </h2>
            <TextField
              fullWidth
              label="Payment Reference Number"
              value={wireReference}
              onChange={(e) => setWireReference(e.target.value)}
              margin="normal"
              placeholder={PAYMENT_DIALOG.WIRE_REFERENCE.PLACEHOLDER}
            />
            <TextField
              fullWidth
              label="Payment Amount (BSD)"
              type="number"
              inputProps={{ min: 0 }}
              value={wirePaymentAmount}
              onChange={(e) => setWirePaymentAmount(e.target.value)}
              margin="normal"
              placeholder={PAYMENT_DIALOG.WIRE_REFERENCE.PAYMENT_PLACEHOLDER}
            />
            <div className="wire-reference-datepicker-container">
              <DateLocalizationProvider>
                <MobileDatePicker
                  className="wire-reference-datepicker"
                  label="Payment Date"
                  format={DATE_FORMAT}
                  maxDate={dayjs()}
                  value={wirePaymentDate}
                  onChange={(newValue) => setWirePaymentDate(newValue)}
                />
              </DateLocalizationProvider>
            </div>
            {wireTransferMutation.error && (
              <p className="error-text">
                Error submitting payment: {wireTransferMutation.error.message}
              </p>
            )}
            <p className="wire-reference-helper-text">
              {PAYMENT_DIALOG.WIRE_REFERENCE.HELPER_TEXT}{" "}
              <span
                className="wire-reference-link"
                onClick={() => {
                  setOpenWireReferenceDialog(false);
                  setOpenPaymentDialog(true);
                }}
              >
                {PAYMENT_DIALOG.WIRE_REFERENCE.LINK_TEXT}
              </span>{" "}
              {PAYMENT_DIALOG.WIRE_REFERENCE.LINK_SUFFIX}
            </p>
            <Button
              variant="contained"
              fullWidth
              onClick={handleWireReferenceSubmit}
              sx={{ mt: 2 }}
              disabled={
                !wireReference.trim() ||
                wirePaymentAmount <= 0 ||
                wirePaymentDate === null ||
                wireTransferMutation.isPending
              }
            >
              {wireTransferMutation.isPending
                ? "Processing..."
                : PAYMENT_DIALOG.WIRE_REFERENCE.SAVE_BUTTON}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openBankDetailsDialog}
        onClose={() => setOpenBankDetailsDialog(false)}
      >
        <DialogContent>
          <div className="bank-details-container">
            <h2>Choose Bank</h2>
            {bankConfigLoading ? (
              <CircularProgress />
            ) : (
              <RadioGroup
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
              >
                <FormControlLabel
                  value={CURRENCY_CODES.BSD}
                  control={<Radio />}
                  label={bankConfig?.data?.bsdBankName || "BSD Bank"}
                />
                <FormControlLabel
                  value={CURRENCY_CODES.USD}
                  control={<Radio />}
                  label={bankConfig?.data?.usdBeneficiaryBank || "USD Bank"}
                />
              </RadioGroup>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={handleBankDetailsContinue}
              sx={{ mt: 2 }}
              disabled={bankConfigLoading}
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {snackbar.open && (
        <SnackbarAlert type={snackbar.type} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </SnackbarAlert>
      )}
    </div>
  );
};

export default Invoices;
